import React from 'react';
import delve from 'dlv';
import useCollapse from 'react-collapsed';

import KeyValuePair from 'components/base/KeyValuePair/KeyValuePair';
import ArtistLinks from 'components/Header/EntityMetadata/ArtistLinks';

import pluralize from 'helpers/pluralize';
import formatDate from 'helpers/formatDate';
import colors from 'styles/themes/colors';
import getEntityUrl from 'url-constructors/getEntityUrl';
import { apiTypeToEntityType } from 'helpers/mapEntityTypes';
import {
  DefaultMetaData,
  Date,
  ExpandableMetaData,
  ToggleDetailsButton,
} from '../EntityMetadata';
import shouldRenderValue from 'helpers/shouldRenderValue';

/** @component */
const Release: React.FC<any> = ({ entity, artists, metadata, showLabel }) => {
  const {
    type,
    id,
    releaseDate,
    is_group: isGroup,
    parent_group_id: parentEntityId,
    group_child_count,
  } = entity;
  const [label] = metadata?.associatedLabelsExcerpt ?? [];

  const entityType = apiTypeToEntityType(type);
  const hasDetails = !(!isGroup && !showLabel);

  const animation = { transitionDuration: '300ms' };
  const { getCollapseProps, getToggleProps, isOpen } = useCollapse({
    collapseStyles: animation,
    expandStyles: animation,
  });

  return (
    <>
      <DefaultMetaData>
        <ArtistLinks artists={artists} />

        {shouldRenderValue(releaseDate) && (
          <Date>{formatDate(releaseDate, 'day-short')}</Date>
        )}
        {!isGroup && shouldRenderValue(id) && (
          <>
            <br />
            <span>{`UPC/EAN: ${id}`}</span>
          </>
        )}
        {hasDetails && (
          <ToggleDetailsButton
            {...getToggleProps()}
            data-pw="toggle-entity-metadata-details"
          >
            {isOpen ? 'Hide details' : 'Show details'}
          </ToggleDetailsButton>
        )}
      </DefaultMetaData>
      {hasDetails && (
        <ExpandableMetaData {...getCollapseProps()}>
          {isGroup && (
            <KeyValuePair
              dataColor={colors.DeepBlue}
              label="Versions"
              value={`${group_child_count} ${pluralize(
                entityType,
                group_child_count
              )}`}
              to={
                group_child_count === 1
                  ? getEntityUrl({
                      entityType,
                      entityId: id,
                      topic: 'overview/view',
                      parentEntityId,
                    })
                  : undefined
              }
              preserveSearch={true}
            />
          )}

          {showLabel && (
            <>
              {!metadata?.associated_label_count && (
                <KeyValuePair label="Labels" value="-" />
              )}
              {metadata?.associated_label_count === 1 && label && (
                <KeyValuePair
                  dataColor={colors.DeepBlue}
                  label="Label"
                  value={delve(label, 'displayName', null)}
                  to={getEntityUrl({
                    entityType: label.type,
                    entityId: label.id,
                    topic: 'overview/summary',
                  })}
                  preserveSearch={true}
                  withChevron
                />
              )}
              {metadata?.associated_label_count > 1 && (
                <KeyValuePair
                  dataColor={colors.DeepBlue}
                  label="Labels"
                  value={`Marketed by ${metadata?.associated_label_count} labels`}
                  preserveSearch={true}
                />
              )}
            </>
          )}
        </ExpandableMetaData>
      )}
    </>
  );
};

export default Release;
